import type * as React from "react";
import { useTranslation } from "react-i18next";
import { useTenant } from "@nasam/core";

import { LoginRouteArtwork } from "./LoginRouteArtwork";

/**
 * Shared shell for the secondary auth screens (OTP, forgot/reset password,
 * activate account). Mirrors the split-screen structure LoginScreen builds
 * inline (node 2676:155617): a dark marketing panel on one side, driven by
 * the same background gradient, corner dots, and truck illustration, and a
 * form column on the other, each panel exactly half the card's width. Each
 * of these secondary Figma frames (OTP 2676:425394, Forgetpassword
 * 2676:426425, Resset Password 2676:426818, Activate Account 2676:431574)
 * repeats this exact panel, differing only in the illustration inside it and
 * the header/body content, so it is factored out here instead of duplicated
 * per screen. LoginScreen keeps its own inline copy of this shell (its
 * content and artwork differ enough not to share this component directly),
 * but the two are kept structurally identical -- same h-dvh/overflow-hidden
 * shell, same .auth-form-panel/.auth-form-content adaptive spacing -- via
 * the shared CSS in styles.css, so one never silently drifts (e.g. gaining a
 * scrollbar) without the other.
 */

export function RequiredMark() {
  return (
    <span className="text-fail-text" aria-hidden="true">
      *
    </span>
  );
}

export interface AuthCardLayoutProps {
  readonly title: React.ReactNode;
  readonly subtitle?: React.ReactNode;
  /** Illustration shown in the marketing panel, e.g. /brand/otp-illustration.png */
  readonly illustrationSrc?: string;
  /**
   * A live artwork that replaces the whole marketing panel (illustration,
   * headline and truck trail), drawn over the login art's grid -- e.g. the
   * OTP screen's lock that echoes the typed code.
   */
  readonly artwork?: React.ReactNode;
  /** Optional: a plain confirmation screen (just a title/subtitle + one
   * footer button, e.g. the reset/activate "success" views) has nothing to
   * put in the scrollable fields area. */
  readonly children?: React.ReactNode;
  /**
   * Action buttons (submit, resend/back-to-login links), rendered in a fixed
   * slot below the scrollable fields area -- structurally separate from
   * `children` so nothing growing inside it (a validation message, an extra
   * password rule) can ever push a button out of view again. A submit
   * button here needs `form="<id>"` matching the <Form>'s own `id` inside
   * `children`, since the two are no longer DOM descendants of each other
   * (the HTML form/button association works across that just fine).
   */
  readonly footer?: React.ReactNode;
}

export function AuthCardLayout({
  title,
  subtitle,
  illustrationSrc,
  artwork,
  children,
  footer,
}: AuthCardLayoutProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";
  const { branding } = useTenant();

  // Mirrors LoginScreen's shell exactly (h-dvh + overflow-hidden, height-
  // driven card, .auth-form-panel/.auth-form-content for the adaptive
  // vertical rhythm) -- that combination is what keeps Login scroll-free, and
  // this card was missing it, which is why OTP/reset/activate could scroll.
  return (
    <main className="relative h-dvh overflow-hidden bg-page-background-light lg:flex lg:min-h-0 lg:items-center lg:justify-center lg:px-5 lg:py-6">
      {/* Decorative layers stay LTR: the artwork is composed, not mirrored. */}
      <div
        aria-hidden="true"
        dir="ltr"
        className="pointer-events-none absolute inset-0"
      >
        <div className="absolute bottom-0 end-0 h-[33.33%] w-[29.63%] bg-[linear-gradient(-38.66deg,var(--color-blue-purple-600)_0%,var(--color-blue-purple-50)_50%)] opacity-20" />
      </div>

      <div
        dir="ltr"
        className="relative h-full w-full lg:flex lg:h-[calc(100dvh-3rem)]"
      >
        <section
          dir={i18n.dir()}
          className={
            (isRtl ? "lg:order-1 " : "lg:order-2 ") +
            "auth-art-panel relative hidden overflow-hidden rounded-lg bg-blue-purple-700 lg:block lg:h-full lg:w-1/2 lg:shrink-0"
          }
        >
          {artwork !== undefined ? (
            <>
              <div aria-hidden="true" className="login-art-grid" />
              <div dir="ltr">{artwork}</div>
            </>
          ) : (
            /* Fixed-ratio stage so the art doesn't stretch on wide panels; see .auth-art-stage in styles.css. */
            <div className="auth-art-stage @container">
              <div aria-hidden="true" dir="ltr" className="absolute inset-0">
                {/* Bottom-anchored and narrower than Figma's 86.8%: the
                  headline/subtitle use fixed font sizes that don't shrink
                  with the stage, so a top-anchored, wider image ran up into
                  the subtitle. At 70% wide it stays in the lower ~56% of the
                  stage. */}
                {illustrationSrc !== undefined && (
                  <img
                    src={illustrationSrc}
                    alt=""
                    className="absolute bottom-[4%] start-[15%] w-[70%]"
                  />
                )}
                <img
                  src="/brand/login-dots.svg"
                  alt=""
                  className="absolute start-[87.62%] top-[2.97%] aspect-square w-[7.73%]"
                />
                <img
                  src="/brand/login-dots.svg"
                  alt=""
                  className="absolute start-[86.55%] top-[89.45%] aspect-square w-[7.73%]"
                />
              </div>

              <div className="absolute inset-x-0 top-[15.16%] flex flex-col items-center gap-4">
                <h2 className="w-full text-balance text-center text-[1.75rem]/[1.3] @[700px]:text-heading-h4-semibold font-extrabold text-page-background-light">
                  {t("authentication:loginMarketingHeadline")}
                </h2>
                <div className="flex w-full items-center justify-center px-4">
                  <p className="line-clamp-2 flex-1 text-balance text-center text-heading-h5-semibold text-blue-purple-100">
                    {t("authentication:loginMarketingSubtitle")}
                  </p>
                </div>
              </div>

              {/* Painted last so the trail runs over the headline and the illustration. */}
              <div aria-hidden="true" dir="ltr" className="absolute inset-0">
                <LoginRouteArtwork mirrored={isRtl} />
              </div>
            </div>
          )}
        </section>

        <section
          dir={i18n.dir()}
          className={
            (isRtl ? "lg:order-2 " : "lg:order-1 ") +
            // No page-level scrollbar -- the *inner* scrollable div below
            // handles any overflow of its own, contained entirely within
            // this fixed-height section (see .auth-form-panel's padding in
            // styles.css). The footer, once present, sits outside that
            // scrolling region entirely so it never scrolls out of view.
            "auth-form-panel animate-rise flex h-full flex-col items-center overflow-hidden lg:min-h-0 lg:w-1/2 lg:rounded-lg"
          }
        >
          <div className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto">
            {/* m-auto: centers vertically while everything fits (the
                original design intent), and top-aligns automatically once
                content is taller than the available space, scrolling from
                the top instead of being clipped or pushed off both edges. */}
            <div className="auth-form-content m-auto flex w-full max-w-[40rem] flex-col">
              <header className="flex w-full flex-col items-start gap-6 px-4 md:px-13">
                <img
                  src={branding?.logoUrl ?? "/brand/logo.png"}
                  alt=""
                  className={
                    branding?.logoUrl === undefined
                      ? "h-[7.0625rem] w-[6.125rem] -scale-y-100"
                      : "h-[7.0625rem] w-[6.125rem] object-contain"
                  }
                />
                <div className="flex w-full flex-col items-start justify-center gap-2">
                  <div className="flex h-13 w-full items-center">
                    <h1 className="w-full text-start text-heading-h3-bold text-text">
                      {title}
                    </h1>
                  </div>
                  {subtitle !== undefined && (
                    <div className="flex w-full items-center px-4">
                      <p className="flex-1 text-start text-body-xl-r text-text-placeholder">
                        {subtitle}
                      </p>
                    </div>
                  )}
                </div>
              </header>

              {children}
            </div>
          </div>

          {footer !== undefined && (
            <div className="mt-4 w-full max-w-[40rem] shrink-0 px-4 md:px-13">
              {footer}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}
